import PropTypes from 'prop-types';

const RECOMMENDATION_DETAILS = {
  design: { label: 'Design', iconClass: 'bi-palette' },
  wording: { label: 'Wording', iconClass: 'bi-fonts' },
  images: { label: 'Images', iconClass: 'bi-image' },
  'job-fit': { label: 'Job fit', iconClass: 'bi-bullseye' },
};

const ScoreCriterion = ({ criterion }) => (
  <li className={`ai-score-criterion is-${criterion.status}`}>
    <div className="ai-score-criterion-heading">
      <span className="ai-score-criterion-icon" aria-hidden="true">
        <i className={`bi ${criterion.passed ? 'bi-check-lg' : 'bi-exclamation-lg'}`}></i>
      </span>
      <strong>{criterion.label}</strong>
      <span className="ai-score-criterion-points">
        {Math.round(criterion.pointsAwarded)} / {Math.round(criterion.weight)}
      </span>
    </div>
    {criterion.description ? <p>{criterion.description}</p> : criterion.passed ? (
      <p>Full points were awarded for this area.</p>
    ) : null}
    {!criterion.passed && criterion.improvement ? (
      <div className="ai-score-criterion-improvement">
        <span>How to improve</span>
        <p>{criterion.improvement}</p>
      </div>
    ) : null}
  </li>
);

const AiScoreResults = ({
  score,
  percentage,
  busy = false,
  onImplement,
  scoreMessageId,
  queuedRecommendationKeys = [],
}) => {
  const scoreData = score && typeof score === 'object'
    ? score
    : { score: Number(score) || 0, maxScore: 100 };
  const safePercentage = Number.isFinite(Number(percentage)) ? Number(percentage) : 0;
  const checklist = Array.isArray(scoreData.checklist) ? scoreData.checklist : [];
  const strengths = Array.isArray(scoreData.strengths) ? scoreData.strengths : [];
  const improvements = Array.isArray(scoreData.improvements) ? scoreData.improvements : [];
  const recommendations = Array.isArray(scoreData.recommendations)
    ? scoreData.recommendations
    : [];
  const recommendationQueued = (index) => Boolean(
    scoreMessageId
    && queuedRecommendationKeys.includes(`${scoreMessageId}:${index}`)
  );
  const queueableRecommendations = recommendations
    .map((recommendation, index) => ({ recommendation, index }))
    .filter(({ recommendation, index }) => (
      !recommendation.applied && !recommendationQueued(index)
    ));
  const passedCriteria = checklist.filter((criterion) => criterion.passed);
  const improvementCriteria = checklist.filter((criterion) => !criterion.passed);
  const isPerfect = scoreData.isPerfect ?? (
    Number(scoreData.score) >= Number(scoreData.maxScore) && Number(scoreData.maxScore) === 100
  );
  const scoreColour = isPerfect
    ? '#198754'
    : safePercentage >= 80
      ? '#0d6efd'
      : safePercentage >= 60
        ? '#fd7e14'
        : '#dc3545';

  return (
    <div className="ai-score-results">
      <div className="ai-score-card mb-3">
        <div
          className="ai-score-ring"
          style={{
            background: `conic-gradient(${scoreColour} ${safePercentage}%, #e9ecef ${safePercentage}% 100%)`,
          }}
        >
          <div className="ai-score-ring-inner">
            <strong>{Math.round(safePercentage)}%</strong>
          </div>
        </div>
        <div className="text-center mt-2">
          <div className="fw-semibold">Portfolio Score</div>
          <div className="small text-muted">
            {Math.round(Number(scoreData.score) || 0)} / {Math.round(Number(scoreData.maxScore) || 100)}
          </div>
          <div className={`ai-score-rating ${isPerfect ? 'is-perfect' : ''}`}>
            {scoreData.ratingLabel || (isPerfect ? 'Perfect' : 'Improvements remain')}
          </div>
        </div>

        {scoreData.summary ? (
          <section className="ai-score-summary-box" aria-labelledby="ai-score-summary-title">
            <strong id="ai-score-summary-title">Summary</strong>
            <p>{scoreData.summary}</p>
          </section>
        ) : null}

        {improvements.length || checklist.length ? (
          <details className="ai-score-details">
            <summary>
              <span className="ai-score-details-toggle">
                <strong>Details</strong>
                <i className="bi bi-chevron-down ai-score-details-chevron" aria-hidden="true"></i>
              </span>
            </summary>
            <div className="ai-score-details-content">
              {improvements.length ? (
                <section className="ai-score-improvements">
                  <h6>Key improvements</h6>
                  <ul>
                    {improvements.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                  </ul>
                </section>
              ) : null}
              {passedCriteria.length ? (
                <section aria-labelledby="ai-score-passed-title">
                  <h6 id="ai-score-passed-title">Passed</h6>
                  <ul className="ai-score-criteria-list">
                    {passedCriteria.map((criterion) => (
                      <ScoreCriterion criterion={criterion} key={criterion.id} />
                    ))}
                  </ul>
                </section>
              ) : null}
              {improvementCriteria.length ? (
                <section aria-labelledby="ai-score-improve-title">
                  <h6 id="ai-score-improve-title">Needs improvement</h6>
                  <ul className="ai-score-criteria-list">
                    {improvementCriteria.map((criterion) => (
                      <ScoreCriterion criterion={criterion} key={criterion.id} />
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          </details>
        ) : null}
      </div>

      {strengths.length ? (
        <section className="mb-3">
          <h6>Strengths</h6>
          <ul className="small mb-0">
            {strengths.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
          </ul>
        </section>
      ) : null}

      {recommendations.length ? (
        <section className="mb-3" aria-labelledby="ai-score-recommendations-title">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <h6 className="mb-0" id="ai-score-recommendations-title">Recommended changes</h6>
            {queueableRecommendations.length > 1 ? (
              <button
                className="btn btn-sm btn-outline-primary"
                type="button"
                disabled={busy}
                onClick={() => queueableRecommendations.forEach(({ recommendation, index }) => {
                  if (scoreMessageId) {
                    onImplement(recommendation, {
                      scoreMessageId,
                      recommendationIndex: index,
                    });
                  } else {
                    onImplement(recommendation);
                  }
                })}
              >
                <i className="bi bi-list-check me-1" aria-hidden="true"></i>
                Queue all
              </button>
            ) : null}
          </div>
          <div className="ai-score-recommendations">
            {recommendations.map((recommendation, index) => {
              const details = RECOMMENDATION_DETAILS[recommendation.category]
                || RECOMMENDATION_DETAILS['job-fit'];
              const applied = Boolean(recommendation.applied);
              const queued = recommendationQueued(index);
              return (
                <article
                  className="ai-score-recommendation"
                  key={`${recommendation.category}-${recommendation.title}-${index}`}
                >
                  <div className="ai-score-recommendation-heading">
                    <span className="ai-score-recommendation-category">
                      <i className={`bi ${details.iconClass}`} aria-hidden="true"></i>
                      {details.label}
                    </span>
                    <span className={`ai-score-priority is-${recommendation.priority}`}>
                      {recommendation.priority}
                    </span>
                  </div>
                  <strong className="small d-block">{recommendation.title}</strong>
                  {recommendation.details ? (
                    <p className="small text-muted mb-2">{recommendation.details}</p>
                  ) : null}
                  <button
                    className={`btn btn-sm w-100 ${
                      applied ? 'btn-outline-success' : queued ? 'btn-outline-secondary' : 'btn-outline-primary'
                    }`}
                    type="button"
                    onClick={() => {
                      if (scoreMessageId) {
                        onImplement(recommendation, { scoreMessageId, recommendationIndex: index });
                        return;
                      }
                      onImplement(recommendation);
                    }}
                    disabled={busy || applied || queued}
                    aria-label={applied
                      ? `${recommendation.title} has been applied`
                      : queued
                        ? `${recommendation.title} is queued`
                        : `Implement ${recommendation.title}`}
                  >
                    <i className={`bi ${
                      applied ? 'bi-check2' : queued ? 'bi-clock' : 'bi-magic'
                    } me-2`} aria-hidden="true"></i>
                    {applied ? 'Applied' : queued ? 'Queued' : 'Implement'}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
};

ScoreCriterion.propTypes = {
  criterion: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['passed', 'improve']).isRequired,
    passed: PropTypes.bool.isRequired,
    pointsAwarded: PropTypes.number.isRequired,
    weight: PropTypes.number.isRequired,
    description: PropTypes.string,
    improvement: PropTypes.string,
  }).isRequired,
};

AiScoreResults.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({
    score: PropTypes.number.isRequired,
    maxScore: PropTypes.number.isRequired,
    summary: PropTypes.string,
    strengths: PropTypes.arrayOf(PropTypes.string),
    improvements: PropTypes.arrayOf(PropTypes.string),
    recommendations: PropTypes.arrayOf(PropTypes.shape({
      category: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      details: PropTypes.string,
      priority: PropTypes.string.isRequired,
      applied: PropTypes.bool,
    })),
    checklist: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['passed', 'improve']).isRequired,
      passed: PropTypes.bool.isRequired,
      pointsAwarded: PropTypes.number.isRequired,
      weight: PropTypes.number.isRequired,
      description: PropTypes.string,
      improvement: PropTypes.string,
    })),
    isPerfect: PropTypes.bool,
    ratingLabel: PropTypes.string,
  })]).isRequired,
  percentage: PropTypes.number.isRequired,
  busy: PropTypes.bool,
  onImplement: PropTypes.func.isRequired,
  scoreMessageId: PropTypes.string,
  queuedRecommendationKeys: PropTypes.arrayOf(PropTypes.string),
};

export default AiScoreResults;
